import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ListingCreator {
  profile: {
    id: string;
    display_name: string | null;
    handle: string | null;
    slug: string | null;
    avatar_url: string | null;
    headline: string | null;
    bio: string | null;
    country_code: string | null;
    account_mode: string | null;
    pro_enabled: boolean | null;
    pro_name: string | null;
    is_ambassador: boolean | null;
    ambassador_title: string | null;
    referral_code: string | null;
  };
  stats: {
    rank: string | null;
    seller_status: string | null;
    completed_orders: number | null;
    unique_buyers: number | null;
    rating_avg: number | null;
    rating_count: number | null;
    on_time_rate: number | null;
    response_rate: number | null;
    identity_verified: boolean | null;
    business_verified: boolean | null;
  } | null;
  rankMeta: { code: string; name: string; sort_order: number } | null;
  services: {
    id: string;
    title: string;
    slug: string;
    short_description: string | null;
    cover_url: string | null;
    starting_price_cents: number;
    delivery_days: number;
    rating_avg: number;
    rating_count: number;
    listing_id: string | null;
  }[];
}

/** Creator behind a listing: public profile, marketplace seller rank and their live services. */
export function listingCreatorQuery(ownerUserId: string | undefined, listingId?: string | undefined) {
  return queryOptions({
    queryKey: ["listing-creator", ownerUserId, listingId],
    enabled: Boolean(ownerUserId),
    queryFn: async (): Promise<ListingCreator | null> => {
      if (!ownerUserId) return null;
      const { data: profile } = await supabase
        .from("profiles")
        .select(
          "id, display_name, handle, slug, avatar_url, headline, bio, country_code, account_mode, pro_enabled, pro_name, is_ambassador, ambassador_title, referral_code",
        )
        .eq("id", ownerUserId)
        .maybeSingle();
      if (!profile) return null;

      const [{ data: stats }, { data: services }] = await Promise.all([
        supabase
          .from("marketplace_seller_stats_public")
          .select(
            "rank, seller_status, completed_orders, unique_buyers, rating_avg, rating_count, on_time_rate, response_rate, identity_verified, business_verified",
          )
          .eq("user_id", ownerUserId)
          .maybeSingle(),
        supabase
          .from("marketplace_services")
          .select(
            "id, title, slug, short_description, cover_url, starting_price_cents, delivery_days, rating_avg, rating_count, listing_id",
          )
          .eq("seller_user_id", ownerUserId)
          .eq("status", "active")
          .is("deleted_at", null)
          .order("hot_score", { ascending: false })
          .limit(6),
      ]);

      let rankMeta: ListingCreator["rankMeta"] = null;
      if (stats?.rank) {
        const { data } = await supabase
          .from("marketplace_seller_ranks")
          .select("code, name, sort_order")
          .eq("code", stats.rank)
          .maybeSingle();
        rankMeta = (data as ListingCreator["rankMeta"]) ?? null;
      }

      return {
        profile: profile as ListingCreator["profile"],
        stats: (stats as ListingCreator["stats"]) ?? null,
        rankMeta,
        services: (services ?? []) as ListingCreator["services"],
      };
    },
  });
}

export function creatorPagePath(profile: ListingCreator["profile"]) {
  const slug = profile.slug || profile.handle || profile.id;
  if (profile.pro_enabled || profile.account_mode === "professional") {
    return { to: "/pro/$slug" as const, params: { slug }, search: { ref: undefined } };
  }
  return { to: "/user/$slug" as const, params: { slug }, search: { ref: undefined } };
}
